import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './AdminPage.css';

// ── Helpers ─────────────────────────────────────────────
const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysLeft = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
};

const whatsappUrl = (phone) => {
    if (!phone) return null;
    const clean = phone.replace(/\D/g, '');
    return `https://wa.me/51${clean}`;
};

// ── Componente Principal ─────────────────────────────────
const AdminPage = () => {
    const [tenants, setTenants]               = useState([]);
    const [isLoading, setIsLoading]           = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editTenant, setEditTenant]         = useState(null);   // tenant siendo editado
    const [resetTenant, setResetTenant]       = useState(null);   // tenant para reset clave
    const [toast, setToast]                   = useState(null);
    const navigate = useNavigate();

    // ── Forms ────────────────────────────────────────────
    const defaultModules = {
        dashboard: true, ventas: true, productos: true, clientes: true,
        fiados: true, gastos: false, inventario: false, pedidos: false,
        reportes: false, cronograma: false, sucursales: false, cajeros: false,
        parking: false
    };

    // Presets de módulos recomendados por tipo de negocio
    const getModulesPreset = (tipo) => {
        if (tipo === 'parking') {
            return {
                dashboard: true, parking: true, reportes: true,
                ventas: false, productos: false, clientes: false,
                fiados: false, gastos: false, inventario: false,
                pedidos: false, cronograma: false, sucursales: false, cajeros: false
            };
        }
        if (tipo === 'botica') {
            return {
                dashboard: true, ventas: true, productos: true, clientes: true,
                fiados: true, gastos: false, inventario: true, pedidos: false,
                reportes: false, cronograma: false, sucursales: false, cajeros: false,
                parking: false
            };
        }
        if (tipo === 'juguteria') {
            return {
                dashboard: true, ventas: true, productos: true, clientes: true,
                fiados: true, gastos: false, inventario: true, pedidos: false,
                reportes: false, cronograma: false, sucursales: false, cajeros: false,
                parking: false
            };
        }
        return {
            dashboard: true, ventas: true, productos: true, clientes: true,
            fiados: true, gastos: false, inventario: false, pedidos: false,
            reportes: false, cronograma: false, sucursales: false, cajeros: false,
            parking: false
        };
    };

    const emptyCreate = { business_name: '', owner_name: '', email: '', password: '', phone: '', business_type: 'general', tipo_negocio: 'tienda_general', modules: defaultModules, limite_sucursales: 1 };
    const [createForm, setCreateForm]   = useState(emptyCreate);
    const [editForm, setEditForm]       = useState({});
    const [newPassword, setNewPassword] = useState('');
    const [isSaving, setIsSaving]       = useState(false);

    useEffect(() => { loadTenants(); }, []);

    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 3500);
            return () => clearTimeout(t);
        }
    }, [toast]);

    const showMsg = (message, type = 'success') => setToast({ message, type });

    // ── Data ─────────────────────────────────────────────
    const loadTenants = async () => {
        setIsLoading(true);
        try {
            const res = await adminAPI.getTenants();
            setTenants(res.data.data.tenants || []);
        } catch {
            showMsg('❌ Error al cargar clientes', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // ── Crear tenant ─────────────────────────────────────
    const handleCreateTenant = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const emailNormalized = createForm.email.includes('@') ? createForm.email : `${createForm.email}@bodega.com`;
            await adminAPI.createTenant({ ...createForm, email: emailNormalized });
            showMsg('✅ Cliente creado exitosamente');
            setShowCreateModal(false);
            setCreateForm(emptyCreate);
            loadTenants();
        } catch (err) {
            showMsg(`❌ ${err.response?.data?.message || 'Error al crear cliente'}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Editar tenant ────────────────────────────────────
    const openEdit = (t) => {
        setEditForm({
            business_name:    t.business_name    || '',
            owner_name:       t.owner_name       || '',
            phone:            t.phone            || '',
            notes:            t.notes            || '',
            tipo_negocio:     t.tipo_negocio     || 'tienda_general',
            trial_ends_at:    t.trial_ends_at ? t.trial_ends_at.slice(0, 10) : '',
            modules:          t.modules          || defaultModules,
            limite_sucursales: t.limite_sucursales || 1,
        });
        setEditTenant(t);
    };

    const handleEditTenant = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await adminAPI.updateTenant(editTenant.id, editForm);
            showMsg('✅ Cliente actualizado');
            setEditTenant(null);
            loadTenants();
        } catch (err) {
            showMsg(`❌ ${err.response?.data?.message || 'Error al actualizar'}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Reset contraseña ─────────────────────────────────
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await adminAPI.resetPassword(resetTenant.id, newPassword);
            showMsg('🔑 Contraseña reseteada correctamente');
            setResetTenant(null);
            setNewPassword('');
        } catch (err) {
            showMsg(`❌ ${err.response?.data?.message || 'Error al resetear'}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Toggle estado ────────────────────────────────────
    const handleToggleStatus = async (tenant) => {
        const newStatus = tenant.business_status === 'activo' ? 'suspendido' : 'activo';
        const action    = newStatus === 'suspendido' ? 'suspender' : 'activar';
        if (!window.confirm(`¿Seguro que deseas ${action} a "${tenant.business_name}"?`)) return;
        try {
            await adminAPI.updateTenantStatus(tenant.id, newStatus);
            showMsg(`✅ Cliente ${newStatus === 'activo' ? 'activado' : 'suspendido'}`);
            loadTenants();
        } catch (err) {
            showMsg(`❌ ${err.response?.data?.message || 'Error al actualizar'}`, 'error');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    // ── Renovar suscripción rápida (+30 días) ────────────
    const handleRenewTenant = async (tenant) => {
        if (!window.confirm(`¿Renovar la suscripción de "${tenant.business_name}" por 30 días más?`)) return;
        
        try {
            // Calcular fecha de hoy + 30 días
            const newDate = new Date();
            newDate.setDate(newDate.getDate() + 30);
            
            // Enviar todos los datos requeridos por el endpoint
            await adminAPI.updateTenant(tenant.id, {
                business_name: tenant.business_name,
                owner_name: tenant.owner_name,
                phone: tenant.phone,
                notes: tenant.notes,
                modules: tenant.modules,
                trial_ends_at: newDate.toISOString().slice(0, 10)
            });

            // Si el cliente estaba bloqueado o suspendido, lo reactivamos
            if (tenant.business_status === 'suspendido') {
                await adminAPI.updateTenantStatus(tenant.id, 'activo');
            }

            showMsg('✅ Suscripción renovada por 30 días');
            loadTenants();
        } catch (err) {
            showMsg(`❌ ${err.response?.data?.message || 'Error al renovar suscripción'}`, 'error');
        }
    };

    const stats = {
        total:     tenants.length,
        active:    tenants.filter(t => t.business_status === 'activo').length,
        suspended: tenants.filter(t => t.business_status === 'suspendido').length,
    };

    const moduleLabels = {
        dashboard: '📊 Dashboard',
        ventas: '💰 Registrar Venta',
        productos: '📦 Productos',
        clientes: '👥 Clientes',
        fiados: '💳 Cuentas por Cobrar',
        gastos: '💸 Gastos',
        inventario: '📋 Inventario',
        pedidos: '🛒 Pedidos',
        reportes: '📈 Reportes',
        cronograma: '📅 Cronograma',
        sucursales: '🏢 Mis Sucursales (Multi-Sucursal)',
        cajeros: '👤 Cajeros y Personal',
        parking: '🚗 Estacionamiento / Parking'
    };

    // ── Render ───────────────────────────────────────────
    return (
        <div className="admin-page">

            {/* HEADER */}
            <header className="admin-header">
                <div className="admin-header-left">
                    <div className="admin-logo">🛡️</div>
                    <div>
                        <h1>Panel SuperAdmin</h1>
                        <p>Gestión de clientes del SaaS ERP</p>
                    </div>
                </div>
                <div className="admin-header-actions">
                    <button onClick={() => setShowCreateModal(true)} className="admin-btn-primary">
                        ➕ Nuevo Cliente
                    </button>
                    <button onClick={handleLogout} className="admin-btn-logout">
                        Cerrar sesión
                    </button>
                </div>
            </header>

            {/* KPIs */}
            <div className="admin-kpis">
                <div className="admin-kpi-card total">
                    <div className="admin-kpi-value">{stats.total}</div>
                    <div className="admin-kpi-label">Total clientes</div>
                </div>
                <div className="admin-kpi-card active">
                    <div className="admin-kpi-value">{stats.active}</div>
                    <div className="admin-kpi-label">Activos</div>
                </div>
                <div className="admin-kpi-card suspended">
                    <div className="admin-kpi-value">{stats.suspended}</div>
                    <div className="admin-kpi-label">Suspendidos</div>
                </div>
            </div>

            {/* TABLA */}
            <div className="admin-table-card">
                <div className="admin-table-header">
                    <h2>Clientes registrados</h2>
                    <button onClick={loadTenants} className="admin-btn-refresh">🔄 Actualizar</button>
                </div>

                {isLoading ? (
                    <div className="admin-loading"><div className="admin-spinner"></div><p>Cargando...</p></div>
                ) : tenants.length === 0 ? (
                    <div className="admin-empty"><p>🏪 No hay clientes registrados aún</p></div>
                ) : (
                    <div className="admin-table-wrapper">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Negocio</th>
                                    <th>Propietario</th>
                                    <th>📱 Teléfono</th>
                                    <th className="center">Ventas</th>
                                    <th className="center">Productos</th>
                                    <th className="center">🏪 Sucursales</th>
                                    <th className="center">⏳ Fin Prueba</th>
                                    <th className="center">Estado</th>
                                    <th className="center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.map(t => {
                                    const days = daysLeft(t.trial_ends_at);
                                    const trialClass = days === null ? '' : days < 0 ? 'trial-expired' : days <= 5 ? 'trial-warning' : 'trial-ok';
                                    const wpUrl = whatsappUrl(t.phone);

                                    return (
                                        <tr key={t.id}>
                                            <td><span className="tenant-id">#{t.id}</span></td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                    <strong>{t.business_name}</strong>
                                                    <span style={{
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        padding: '2px 6px',
                                                        borderRadius: '6px',
                                                        background: t.tipo_negocio === 'parking' ? 'rgba(245, 158, 11, 0.15)' : t.tipo_negocio === 'botica' ? 'rgba(16, 185, 129, 0.15)' : t.tipo_negocio === 'juguteria' ? 'rgba(168, 85, 247, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                                                        color: t.tipo_negocio === 'parking' ? '#f59e0b' : t.tipo_negocio === 'botica' ? '#10b981' : t.tipo_negocio === 'juguteria' ? '#a855f7' : '#3b82f6',
                                                        border: `1px solid ${t.tipo_negocio === 'parking' ? 'rgba(245, 158, 11, 0.3)' : t.tipo_negocio === 'botica' ? 'rgba(16, 185, 129, 0.3)' : t.tipo_negocio === 'juguteria' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`
                                                    }}>
                                                        {t.tipo_negocio === 'parking' ? '🚗 Parking' : t.tipo_negocio === 'botica' ? '💊 Botica' : t.tipo_negocio === 'juguteria' ? '🧩 Jugutería' : '🛒 Tienda'}
                                                    </span>
                                                </div>
                                                {t.notes && (
                                                    <div className="tenant-notes" title={t.notes}>
                                                        📝 {t.notes.length > 40 ? t.notes.slice(0, 40) + '…' : t.notes}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{t.owner_name}</td>
                                            <td>
                                                {wpUrl ? (
                                                    <a href={wpUrl} target="_blank" rel="noopener noreferrer" className="whatsapp-link">
                                                        <span className="whatsapp-icon">💬</span> {t.phone}
                                                    </a>
                                                ) : (
                                                    <span className="no-phone">Sin teléfono</span>
                                                )}
                                            </td>
                                            <td className="center">{t.total_sales || 0}</td>
                                            <td className="center">{t.total_products || 0}</td>
                                            <td className="center">
                                                {/* Pill de sucursales con semáforo */}
                                                {(() => {
                                                    const hasSucursales = !!(t.modules?.sucursales);
                                                    if (!hasSucursales) {
                                                        return (
                                                            <span style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                                                background: 'rgba(148, 163, 184, 0.1)', color: '#94a3b8', borderRadius: 100,
                                                                padding: '3px 10px', fontWeight: 600, fontSize: 12,
                                                                border: '1px solid rgba(148, 163, 184, 0.2)'
                                                            }}>
                                                                👤 Local Único
                                                            </span>
                                                        );
                                                    }
                                                    const activas = parseInt(t.sucursales_activas || 0);
                                                    const limite  = parseInt(t.limite_sucursales  || 1);
                                                    const pct     = activas / limite;
                                                    const color   = pct >= 1 ? '#ef4444' : pct >= 0.8 ? '#f59e0b' : '#22c55e';
                                                    const bg      = pct >= 1 ? 'rgba(239,68,68,0.12)' : pct >= 0.8 ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.12)';
                                                    return (
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 5,
                                                            background: bg, color, borderRadius: 100,
                                                            padding: '3px 10px', fontWeight: 700, fontSize: 13,
                                                            border: `1px solid ${color}44`
                                                        }}>
                                                            🏪 {activas} / {limite}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="center">
                                                <span className={`trial-badge ${trialClass}`}>
                                                    {days === null ? '—' :
                                                     days < 0     ? 'Expirado' :
                                                     days === 0   ? 'Hoy vence' :
                                                                    `${days}d restantes`}
                                                </span>
                                                <div className="trial-date">{formatDate(t.trial_ends_at)}</div>
                                            </td>
                                            <td className="center">
                                                <span className={`admin-status-badge ${t.business_status}`}>
                                                    {t.business_status === 'activo' ? '🟢 Activo' : '🔴 Suspendido'}
                                                </span>
                                            </td>
                                            <td className="center">
                                                <div className="action-btns">
                                                    <button onClick={() => openEdit(t)} className="admin-action-btn edit" title="Editar cliente">
                                                        ✏️
                                                    </button>
                                                    <button onClick={() => handleRenewTenant(t)} className="admin-action-btn" title="Añadir 30 días de suscripción" style={{ background: '#10b981', color: 'white' }}>
                                                        +30d
                                                    </button>
                                                    <button onClick={() => { setResetTenant(t); setNewPassword(''); }} className="admin-action-btn reset" title="Resetear contraseña">
                                                        🔑
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleStatus(t)}
                                                        className={`admin-toggle-btn ${t.business_status === 'activo' ? 'suspend' : 'activate'}`}
                                                    >
                                                        {t.business_status === 'activo' ? 'Suspender' : 'Activar'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* MODAL — CREAR TENANT */}
            {showCreateModal && (
                <div className="admin-modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3>🏪 Nuevo Cliente</h3>
                            <button onClick={() => setShowCreateModal(false)} className="admin-modal-close">✕</button>
                        </div>
                        <form onSubmit={handleCreateTenant} className="admin-form">
                            <div className="admin-form-grid">
                                <div className="admin-form-group">
                                    <label>Nombre del negocio *</label>
                                    <input required value={createForm.business_name} onChange={e => setCreateForm({ ...createForm, business_name: e.target.value })} placeholder="Ej: Bodega Don Luis" />
                                </div>
                                <div className="admin-form-group">
                                    <label>Propietario *</label>
                                    <input required value={createForm.owner_name} onChange={e => setCreateForm({ ...createForm, owner_name: e.target.value })} placeholder="Nombre completo" />
                                </div>
                                <div className="admin-form-group">
                                    <label>Email *</label>
                                    <input type="text" required value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} placeholder="correo@ejemplo.com o nombre de usuario" />
                                </div>
                                <div className="admin-form-group">
                                    <label>Contraseña *</label>
                                    <input type="password" required minLength={6} value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Mínimo 6 caracteres" />
                                </div>
                                <div className="admin-form-group">
                                    <label>Teléfono / WhatsApp</label>
                                    <input value={createForm.phone} onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} placeholder="Ej: 987654321" />
                                </div>
                                <div className="admin-form-group">
                                    <label>Modo de Operación / ERP</label>
                                    <select 
                                        value={createForm.tipo_negocio} 
                                        onChange={e => {
                                            const newTipo = e.target.value;
                                            setCreateForm(prev => ({
                                                ...prev,
                                                tipo_negocio: newTipo,
                                                modules: getModulesPreset(newTipo)
                                            }));
                                        }}
                                    >
                                        <option value="tienda_general">🛒 Tienda General / Minimarket / Bodega</option>
                                        <option value="botica">💊 Botica / Farmacia (Lotes, FEFO y Semáforo)</option>
                                        <option value="parking">🚗 Parking / Estacionamiento (Por Estadía, Aeropuerto)</option>
                                        <option value="juguteria">🧩 Jugutería / Armables (Sets, Piezas y Dificultad)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Modo Multi-Sucursal vs Negocio Propio */}
                            {createForm.modules.sucursales ? (
                                <div className="admin-form-group" style={{ marginTop: '12px', padding: '14px', background: 'rgba(99,102,241,0.1)', borderRadius: '10px', border: '1px solid rgba(99,102,241,0.3)' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#a5b4fc' }}>
                                        🏢 Plan Multi-Sucursal — Límite de Locales
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                        <input
                                            id="create-limite-sucursales"
                                            type="number"
                                            min={2}
                                            max={50}
                                            required
                                            value={createForm.limite_sucursales < 2 ? 2 : createForm.limite_sucursales}
                                            onChange={e => setCreateForm({ ...createForm, limite_sucursales: parseInt(e.target.value) || 2 })}
                                            style={{ width: 100 }}
                                        />
                                        <span style={{ fontSize: 13, color: '#c7d2fe' }}>sucursales permitidas</span>
                                    </div>
                                    <p style={{ margin: '6px 0 0', fontSize: 12, color: '#94a3b8' }}>
                                        El cliente podrá crear locales y gestionar cajeros por sucursal hasta este límite.
                                    </p>
                                </div>
                            ) : (
                                <div style={{ marginTop: '12px', padding: '12px 14px', background: 'rgba(34,197,94,0.08)', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.25)', display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontSize: 20 }}>👤</span>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#4ade80' }}>
                                            Modo Negocio Propio (Local Único)
                                        </div>
                                        <div style={{ fontSize: 12, color: '#94a3b8' }}>
                                            Opera con 1 solo local directo. Para darle varias sucursales, marca la casilla <strong>"Mis Sucursales"</strong> abajo.
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="admin-form-group admin-form-group-full" style={{ marginTop: '15px' }}>
                                <label>📦 Módulos activos del ERP</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginTop: '10px' }}>
                                    {Object.entries(moduleLabels).map(([key, label]) => (
                                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', padding: '6px 8px', borderRadius: 6, background: createForm.modules[key] ? 'rgba(99,102,241,0.1)' : 'transparent', border: `1px solid ${createForm.modules[key] ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)'}` }}>
                                            <input 
                                                type="checkbox" 
                                                checked={!!createForm.modules[key]} 
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setCreateForm(prev => {
                                                        const updated = { ...prev.modules, [key]: checked };
                                                        if (key === 'sucursales' && checked && !updated.cajeros) {
                                                            updated.cajeros = true; // sugerir cajeros al activar sucursales
                                                        }
                                                        return {
                                                            ...prev,
                                                            limite_sucursales: key === 'sucursales' && checked ? (prev.limite_sucursales < 2 ? 2 : prev.limite_sucursales) : 1,
                                                            modules: updated
                                                        };
                                                    });
                                                }}
                                            />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="admin-form-actions">
                                <button type="button" onClick={() => setShowCreateModal(false)} className="admin-btn-cancel">Cancelar</button>
                                <button type="submit" disabled={isSaving} className="admin-btn-primary">
                                    {isSaving ? '⏳ Creando...' : '✅ Crear Cliente'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL — EDITAR TENANT */}
            {editTenant && (
                <div className="admin-modal-overlay" onClick={() => setEditTenant(null)}>
                    <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3>✏️ Editar — {editTenant.business_name}</h3>
                            <button onClick={() => setEditTenant(null)} className="admin-modal-close">✕</button>
                        </div>
                        <form onSubmit={handleEditTenant} className="admin-form">
                            <div className="admin-form-grid">
                                <div className="admin-form-group">
                                    <label>Nombre del negocio</label>
                                    <input value={editForm.business_name} onChange={e => setEditForm({ ...editForm, business_name: e.target.value })} placeholder="Nombre del negocio" />
                                </div>
                                <div className="admin-form-group">
                                    <label>Propietario</label>
                                    <input value={editForm.owner_name} onChange={e => setEditForm({ ...editForm, owner_name: e.target.value })} placeholder="Nombre del propietario" />
                                </div>
                                <div className="admin-form-group">
                                    <label>📱 Teléfono / WhatsApp</label>
                                    <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Ej: 987654321" />
                                </div>
                                <div className="admin-form-group">
                                    <label>Modo de Operación / ERP</label>
                                    <select 
                                        value={editForm.tipo_negocio} 
                                        onChange={e => {
                                            const newTipo = e.target.value;
                                            setEditForm(prev => ({
                                                ...prev,
                                                tipo_negocio: newTipo,
                                                modules: { ...prev.modules, parking: newTipo === 'parking' }
                                            }));
                                        }}
                                    >
                                        <option value="tienda_general">🛒 Tienda General / Minimarket / Bodega</option>
                                        <option value="botica">💊 Botica / Farmacia (Lotes, FEFO y Semáforo)</option>
                                        <option value="parking">🚗 Parking / Estacionamiento (Por Estadía, Aeropuerto)</option>
                                        <option value="juguteria">🧩 Jugutería / Armables (Sets, Piezas y Dificultad)</option>
                                    </select>
                                </div>
                                <div className="admin-form-group">
                                    <label>⏳ Fin de prueba</label>
                                    <input type="date" value={editForm.trial_ends_at} onChange={e => setEditForm({ ...editForm, trial_ends_at: e.target.value })} />
                                </div>
                                
                                {/* 🏪 Límite de sucursales según si tiene el módulo activo */}
                                <div className="admin-form-group" style={{ gridColumn: '1 / -1' }}>
                                    {editForm.modules?.sucursales ? (
                                        <div style={{ background: 'rgba(99,102,241,0.08)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(99,102,241,0.25)' }}>
                                            <label style={{ display:'flex', alignItems:'center', justifyContent: 'space-between', fontWeight:700, color:'#a5b4fc' }}>
                                                <span>🏢 Límite de Sucursales del Plan</span>
                                                <span style={{ fontSize:12, fontWeight:500, color:'#94a3b8' }}>
                                                    Actualmente: {editTenant.sucursales_activas || 0} / {editForm.limite_sucursales || 1} locales creados
                                                </span>
                                            </label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                                <input
                                                    id="edit-limite-sucursales"
                                                    type="number"
                                                    min={1}
                                                    max={50}
                                                    value={editForm.limite_sucursales}
                                                    onChange={e => setEditForm({ ...editForm, limite_sucursales: parseInt(e.target.value) || 1 })}
                                                    style={{ width:100 }}
                                                />
                                                <span style={{ fontSize: 13, color: '#c7d2fe' }}>sucursales permitidas</span>
                                            </div>
                                            <p style={{ margin:'6px 0 0', fontSize:12, color:'#94a3b8' }}>
                                                Aumenta el número para permitirle abrir más locales.
                                            </p>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '12px 14px', background: 'rgba(34,197,94,0.08)', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.25)', display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontSize: 20 }}>👤</span>
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#4ade80' }}>
                                                    Modo Negocio Propio (Local Único)
                                                </div>
                                                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                                                    Este cliente no maneja sucursales múltiples. Para convertirlo a Multi-Sucursal, marca la casilla <strong>"Mis Sucursales"</strong> abajo.
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="admin-form-group admin-form-group-full" style={{ margin: '15px 0' }}>
                                <label>📦 Módulos activos del ERP</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginTop: '10px' }}>
                                    {Object.entries(moduleLabels).map(([key, label]) => (
                                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', padding: '6px 8px', borderRadius: 6, background: editForm.modules?.[key] ? 'rgba(99,102,241,0.1)' : 'transparent', border: `1px solid ${editForm.modules?.[key] ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)'}` }}>
                                            <input 
                                                type="checkbox" 
                                                checked={editForm.modules?.[key] || false} 
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setEditForm(prev => {
                                                        const updated = { ...prev.modules, [key]: checked };
                                                        if (key === 'sucursales' && checked && !updated.cajeros) {
                                                            updated.cajeros = true; // sugerir cajeros al activar sucursales
                                                        }
                                                        return {
                                                            ...prev,
                                                            limite_sucursales: key === 'sucursales' && checked ? (prev.limite_sucursales < 2 ? 2 : prev.limite_sucursales) : (key === 'sucursales' && !checked ? 1 : prev.limite_sucursales),
                                                            modules: updated
                                                        };
                                                    });
                                                }}
                                            />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="admin-form-group admin-form-group-full">
                                <label>📝 Notas internas</label>
                                <textarea
                                    rows={3}
                                    value={editForm.notes}
                                    onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                                    placeholder="Ej: Le di la clave el 2 de abril. Vive en Av. Atarjea 123. Prefiere ser contactado por WhatsApp..."
                                />
                            </div>
                            <div className="admin-form-actions">
                                <button type="button" onClick={() => setEditTenant(null)} className="admin-btn-cancel">Cancelar</button>
                                <button type="submit" disabled={isSaving} className="admin-btn-primary">
                                    {isSaving ? '⏳ Guardando...' : '💾 Guardar cambios'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL — RESET CONTRASEÑA */}
            {resetTenant && (
                <div className="admin-modal-overlay" onClick={() => setResetTenant(null)}>
                    <div className="admin-modal admin-modal-sm" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3>🔑 Resetear contraseña</h3>
                            <button onClick={() => setResetTenant(null)} className="admin-modal-close">✕</button>
                        </div>
                        <form onSubmit={handleResetPassword} className="admin-form">
                            <p className="reset-context">
                                Cliente: <strong>{resetTenant.business_name}</strong><br/>
                                {resetTenant.phone && (
                                    <span className="reset-context-sub">
                                        Puedes avisarle por WhatsApp: <a href={whatsappUrl(resetTenant.phone)} target="_blank" rel="noopener noreferrer">💬 {resetTenant.phone}</a>
                                    </span>
                                )}
                            </p>
                            <div className="admin-form-group">
                                <label>Nueva contraseña *</label>
                                <input
                                    type="password"
                                    required
                                    minLength={6}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    placeholder="Mínimo 6 caracteres"
                                    autoFocus
                                />
                            </div>
                            <div className="admin-form-actions" style={{ marginTop: '16px' }}>
                                <button type="button" onClick={() => setResetTenant(null)} className="admin-btn-cancel">Cancelar</button>
                                <button type="submit" disabled={isSaving || newPassword.length < 6} className="admin-btn-reset">
                                    {isSaving ? '⏳ Reseteando...' : '🔑 Cambiar clave'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* TOAST */}
            {toast && (
                <div className={`admin-toast ${toast.type}`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
};

export default AdminPage;
